import { useState } from "react";
import { X, FileText, Check } from "lucide-react";
import styles from "~/styles/burn-rate.module.css";

export type QuoteTheme = "dark" | "light" | "minimal" | "corporate";

export interface QuoteConfig {
  clientName: string;
  projectDescription: string;
  includeSummary: boolean;
  includeBreakdown: boolean;
  includeCharts: boolean;
  includeScenarios: boolean;
  includeBreakeven: boolean;
  includeNotes: boolean;
  theme: QuoteTheme;
}

const THEME_OPTIONS: { id: QuoteTheme; label: string }[] = [
  { id: "dark", label: "Oscuro" },
  { id: "light", label: "Claro" },
  { id: "minimal", label: "Minimal" },
  { id: "corporate", label: "Corporativo" },
];

const SECTIONS: {
  key: keyof Omit<QuoteConfig, "clientName" | "projectDescription" | "theme">;
  label: string;
}[] = [
  { key: "includeSummary", label: "Resumen ejecutivo" },
  { key: "includeBreakdown", label: "Desglose de costos" },
  { key: "includeCharts", label: "Gráficos visuales" },
  { key: "includeScenarios", label: "Escenarios de margen" },
  { key: "includeBreakeven", label: "Punto de equilibrio" },
  { key: "includeNotes", label: "Notas legales" },
];

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (config: QuoteConfig) => void;
}

export function QuoteModal({ isOpen, onClose, onGenerate }: QuoteModalProps) {
  const [config, setConfig] = useState<QuoteConfig>({
    clientName: "",
    projectDescription: "",
    includeSummary: true,
    includeBreakdown: true,
    includeCharts: false,
    includeScenarios: false,
    includeBreakeven: true,
    includeNotes: true,
    theme: "dark",
  });

  if (!isOpen) return null;

  const toggle = (key: keyof QuoteConfig) => {
    setConfig((p) => ({ ...p, [key]: !p[key] }));
  };

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.modalBackdrop} onClick={onClose} />
      <div className={styles.modalCard} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderText}>
            <p className={styles.modalTag}>COTIZACION</p>
            <h2 className={styles.modalTitle}>Generar propuesta</h2>
          </div>
          <button className={styles.modalClose} onClick={onClose} aria-label="Cerrar">
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        {/* Scroll content */}
        <div className={styles.modalScroll}>
          {/* Client */}
          <div className={styles.modalSection}>
            <p className={styles.modalSectionTitle}>Cliente</p>
            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Nombre</label>
              <div className={styles.inputCardInner}>
                <input
                  className={styles.inputCardField}
                  placeholder="Ej. Acme Corp"
                  value={config.clientName}
                  onChange={(e) => setConfig((p) => ({ ...p, clientName: e.target.value }))}
                  maxLength={64}
                />
              </div>
            </div>
            <div className={styles.modalField} style={{ marginTop: 10 }}>
              <label className={styles.modalLabel}>Descripcion del proyecto</label>
              <div className={styles.inputCardInner}>
                <input
                  className={styles.inputCardField}
                  placeholder="Ej. Desarrollo web + branding"
                  value={config.projectDescription}
                  onChange={(e) => setConfig((p) => ({ ...p, projectDescription: e.target.value }))}
                  maxLength={120}
                />
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className={styles.modalSection}>
            <p className={styles.modalSectionTitle}>Secciones a incluir</p>
            <div className={styles.modalToggleList}>
              {SECTIONS.map(({ key, label }) => (
                <label key={key} className={styles.modalToggleRow}>
                  <span className={styles.modalToggleLabel}>{label}</span>
                  <input
                    type="checkbox"
                    className={styles.switch}
                    checked={config[key] as boolean}
                    onChange={() => toggle(key)}
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Theme */}
          <div className={styles.modalSection}>
            <p className={styles.modalSectionTitle}>Tema visual</p>
            <div className={styles.modalThemeGrid}>
              {THEME_OPTIONS.map((t) => (
                <button
                  key={t.id}
                  className={`${styles.modalThemeBtn} ${config.theme === t.id ? styles.modalThemeBtnActive : ""}`}
                  onClick={() => setConfig((p) => ({ ...p, theme: t.id }))}
                >
                  {config.theme === t.id && <Check size={11} strokeWidth={2} />}
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer action */}
        <div className={styles.modalFooter}>
          <button
            className={styles.modalGenerateBtn}
            onClick={() => onGenerate(config)}
          >
            <FileText size={14} strokeWidth={1.5} />
            Generar cotizacion
          </button>
        </div>
      </div>
    </div>
  );
}
